/**
 * Document versions (cdb_tqs#1974, #1543): which upload continues which
 * document, the version list of a document, and the refusals of a version
 * upload. Per organisation (`features.document_versions`, client-gated, never
 * per user); every caller here also works against a backend without it.
 */
import { tqsRequest } from './video'

export interface VersionCandidate {
  document_id: string
  title: string | null
  filename: string
  version_number: number
  version_chain_id: string
  status: string
  created_at: string | null
}

export interface VersionCandidates {
  /** False while versions are off for the area's organisation. */
  enabled: boolean
  /** Lower-cased file name → current documents of that name, newest first. */
  candidates: Record<string, VersionCandidate[]>
}

export type DocumentVersionState = 'current' | 'superseded' | 'archived'

export interface DocumentVersion {
  document_id: string
  title: string | null
  filename: string
  version_number: number
  version_state: DocumentVersionState
  previous_version_id: string | null
  valid_from: string | null
  status: string
  created_at: string | null
}

/** The codes a version upload is refused with (cdb_tqs#1974 1.2). */
export const VERSION_ERROR_CODES = [
  'document_versions_disabled',
  'version_target_not_found',
  'version_target_other_area',
  'version_target_not_current',
  'identical_to_current_version',
  'version_conflict',
] as const
export type VersionErrorCode = (typeof VERSION_ERROR_CODES)[number]

export class DocumentVersionError extends Error {
  readonly code: VersionErrorCode
  readonly status: number
  /** The version the upload would have replaced, when the server names it. */
  readonly versionNumber: number | null

  constructor(status: number, code: VersionErrorCode, message: string, versionNumber: number | null) {
    super(message)
    this.name = 'DocumentVersionError'
    this.status = status
    this.code = code
    this.versionNumber = versionNumber
  }
}

/** A refused upload's version error, or null when it is any other refusal. */
export function parseVersionError(status: number, data: unknown): DocumentVersionError | null {
  const detail = data && typeof data === 'object' ? (data as { detail?: unknown }).detail : null
  if (!detail || typeof detail !== 'object') return null
  const { code, message, version_number: versionNumber } = detail as Record<string, unknown>
  if (typeof code !== 'string' || !(VERSION_ERROR_CODES as readonly string[]).includes(code)) return null
  return new DocumentVersionError(
    status,
    code as VersionErrorCode,
    typeof message === 'string' ? message : code,
    typeof versionNumber === 'number' ? versionNumber : null,
  )
}

/** Current documents of `knowledgeArea` named like `filenames` (without case). */
export async function fetchVersionCandidates(knowledgeArea: string, filenames: string[]): Promise<VersionCandidates> {
  const names = [...new Set(filenames.filter((n) => n.trim()))]
  if (!names.length) return { enabled: false, candidates: {} }
  const r = await tqsRequest<VersionCandidates>('documents/version-candidates', {
    method: 'POST',
    body: { knowledge_area: knowledgeArea, filenames: names.slice(0, 500) },
  })
  // A backend without the route, or one refusing the area: no proposal.
  if (!r.ok || !r.data) return { enabled: false, candidates: {} }
  return { enabled: r.data.enabled === true, candidates: r.data.candidates ?? {} }
}

/** Every version of the document's chain, newest first. */
export async function listDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
  const r = await tqsRequest<{ versions: DocumentVersion[] }>(
    `documents/${encodeURIComponent(documentId)}/versions`,
  )
  if (r.status === 404) return []
  if (!r.ok || !r.data) throw new Error(`Document versions not loaded: ${r.status}`)
  return r.data.versions
}

/**
 * The default for every selected file (decided 2026-10-26): a file named like
 * a current document of the area becomes its new version; the newest such
 * document when there are several. Files without a match map to nothing.
 */
export function defaultVersionTargets(fileNames: string[], found: VersionCandidates): Map<string, VersionCandidate> {
  const targets = new Map<string, VersionCandidate>()
  if (!found.enabled) return targets
  for (const name of fileNames) {
    const match = found.candidates[name.toLowerCase()]?.[0]
    if (match) targets.set(name, match)
  }
  return targets
}
